import { create } from 'zustand';

export interface FavoriteWithClass {
  id: string;
  user_id: string;
  class_id: string;
  created_at: string;
  class?: {
    id: string;
    title: string;
    thumbnail_url: string | null;
    start_date: string;
    end_date: string;
    status: string;
    provider_id: string;
  };
}

interface FavoritesStore {
  favorites: FavoriteWithClass[];
  loaded: boolean;
  loading: boolean;
  error: Error | null;

  fetchFavorites: () => Promise<void>;
  toggleFavorite: (classId: string) => Promise<boolean>;
  isFavorited: (classId: string) => boolean;
  clear: () => void;
}

export const useFavoritesStore = create<FavoritesStore>((set, get) => ({
  favorites: [],
  loaded: false,
  loading: false,
  error: null,

  fetchFavorites: async () => {
    set({ loading: true, error: null });
    try {
      const response = await fetch('/api/v1/favorites', {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          set({ favorites: [], loaded: true, loading: false });
          return;
        }
        throw new Error('관심 수업 목록을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      set({ favorites: data.favorites || [], loaded: true, loading: false });
    } catch (error) {
      set({ error: error as Error, loading: false });
      console.error('Failed to fetch favorites:', error);
    }
  },

  toggleFavorite: async (classId) => {
    const wasFavorited = get().isFavorited(classId);

    // Optimistic update
    if (wasFavorited) {
      set((state) => ({
        favorites: state.favorites.filter((f) => f.class_id !== classId),
      }));
    } else {
      const optimisticFavorite: FavoriteWithClass = {
        id: `optimistic-${classId}`,
        user_id: '',
        class_id: classId,
        created_at: new Date().toISOString(),
      };
      set((state) => ({
        favorites: [optimisticFavorite, ...state.favorites],
      }));
    }

    try {
      const method = wasFavorited ? 'DELETE' : 'POST';
      const response = await fetch(`/api/v1/classes/${classId}/favorite`, {
        method,
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || '관심 수업 처리에 실패했습니다.');
      }

      // Refresh server-truth (특히 추가 시 class join 데이터를 받아오기 위함)
      await get().fetchFavorites();
      return !wasFavorited;
    } catch (error) {
      // Rollback
      if (wasFavorited) {
        await get().fetchFavorites();
      } else {
        set((state) => ({
          favorites: state.favorites.filter((f) => f.id !== `optimistic-${classId}`),
        }));
      }
      console.error('Failed to toggle favorite:', error);
      throw error;
    }
  },

  isFavorited: (classId) => {
    return get().favorites.some((f) => f.class_id === classId);
  },

  clear: () => {
    set({ favorites: [], loaded: false, loading: false, error: null });
  },
}));
